import { ArrowLeft, Compass, Clock3, Home } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import type { BetaHeldFeature } from '../../config/beta-surface';

const HOLD_COPY: Partial<Record<BetaHeldFeature, { title: string; body: string }>> = {
  'map-courses': {
    title: 'Country courses are being refined',
    body: 'The geography foundation is live for beta: maps, capitals, quizzes, and field trips. The deeper country-course sequence is staying in polish until it feels excellent.',
  },
  'map-expeditions': {
    title: 'Expeditions are coming later',
    body: 'We are keeping beta focused on maps, capitals, quizzes, and parks. Story expeditions will arrive once they are tighter and easier to follow.',
  },
  'map-journeys': {
    title: 'Story journeys are on the refinement track',
    body: 'The core map explorer is ready for beta. Longer multi-stop journeys are being held until they feel more polished.',
  },
  'map-printables': {
    title: 'Printables are being tuned',
    body: 'We are holding printable packs for now so the beta experience stays clean and screen-first.',
  },
  'map-puzzle': {
    title: 'Map puzzle is being refined',
    body: 'States, capitals, country hover, and map quizzes are ready for beta. The puzzle modes are staying in polish.',
  },
  'nature-id': {
    title: 'Nature Identifier is being refined',
    body: 'We are holding this camera feature out of the first beta surface while the core parks and maps experience gets family feedback.',
  },
  jobs: {
    title: 'Careers are coming after beta',
    body: 'The first beta stays centered on lessons, languages, maps, parks, games, creation, and parent progress.',
  },
  competitions: {
    title: 'Competitions are coming later',
    body: 'Sibling and family competition modes need a little more polish before they become part of the beta experience.',
  },
  reports: {
    title: 'Advanced reports are being refined',
    body: 'Parents can still see progress during beta. Formal reports and transcripts are staying out until the workflow is sharper.',
  },
  filings: {
    title: 'Filings are not in beta yet',
    body: 'Compliance helpers need a higher bar before we put them in front of beta families.',
  },
  'annual-report': {
    title: 'Annual reports are not in beta yet',
    body: 'We are holding formal reporting until the parent workflows are fully tightened.',
  },
  pricing: {
    title: 'Plans are not open yet',
    body: 'The beta is focused on learning feedback, not purchase decisions.',
  },
};

function copyFor(feature: BetaHeldFeature): { title: string; body: string } {
  return HOLD_COPY[feature] ?? {
    title: 'This area is being refined',
    body: 'We are keeping the beta focused on the strongest family workflows and will open this area after more polish.',
  };
}

export function BetaHoldPage({ feature }: { feature: BetaHeldFeature }) {
  const navigate = useNavigate();
  const copy = copyFor(feature);

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col justify-center px-4 py-10">
      <div className="rounded-3xl border border-sky-100 bg-white p-6 text-center shadow-sm dark:border-sky-900/60 dark:bg-gray-900">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-50 text-sky-600 dark:bg-sky-900/40 dark:text-sky-200">
          <Clock3 className="h-7 w-7" aria-hidden="true" />
        </div>
        <h1 className="text-xl font-extrabold text-gray-900 dark:text-white">{copy.title}</h1>
        <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300">{copy.body}</p>

        <div className="mt-6 grid gap-2 sm:grid-cols-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-gray-200 px-4 py-2.5 text-sm font-bold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back
          </button>
          <Link
            to="/learn?tab=explore"
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-sky-700"
          >
            <Compass className="h-4 w-4" aria-hidden="true" />
            Explore
          </Link>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gray-900 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
          >
            <Home className="h-4 w-4" aria-hidden="true" />
            Today
          </Link>
        </div>
      </div>
    </div>
  );
}

